import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

// SETUP COLORS

// Paleta de marca Consuman (branding 2026).
// Los 7 colores oficiales, tal cual salen del manual:
export const BRAND = {
  white: "#F8F8F8",
  offWhite: "#F4F2F3",
  purple: "#6F26FF",
  lila: "#AE6BFF",
  black: "#1F1F1F",
  coral: "#FF8880",
  yellow: "#FFA538",
  purpleTint: "#F9F6FF",
  darkNav: "#141414", // sidebar: un paso por debajo del fondo de página
  darkBg: "#1F1F1F", // fondo de página = negro de marca
  darkPaper: "#282828", // cards: un paso por encima del fondo
};

// Superficie del sidebar. Branding 2026: es oscuro en los DOS modos del tema,
// así que no sale de `background.*`; en dark baja un paso por debajo del fondo
// de página para que el sidebar siga separándose del contenido.
export const NAV_SURFACE = {
  light: BRAND.black,
  dark: BRAND.darkNav,
};

export const navSurface = theme =>
  theme.palette.mode === "light" ? NAV_SURFACE.light : NAV_SURFACE.dark;

// Rampa neutra: los grises no llevan tinte violeta, sólo los dos neutros
// de marca en los extremos claros (#F8F8F8 / #F4F2F3) y el negro (#1F1F1F).
const GREY = {
  0: "#FFFFFF",
  100: BRAND.white,
  200: BRAND.offWhite,
  300: "#E3E3E4",
  400: "#C4C4C6",
  500: "#949497",
  600: "#6A6A6D",
  700: "#47474A",
  800: "#2A2A2C",
  900: BRAND.black,
};

const PRIMARY = {
  lighter: "#EADCFF",
  light: BRAND.lila,
  main: BRAND.purple,
  dark: "#5518CC",
  darker: "#2E0B73",
  contrastText: "#FFFFFF",
};

const SECONDARY = {
  lighter: "#F2E7FF",
  light: "#C99AFF",
  main: BRAND.lila,
  dark: "#8542D6",
  darker: "#4B1B85",
  // El manual usa negro sobre lila, y además es el único que da contraste real
  contrastText: BRAND.black,
};

const INFO = {
  lighter: "#D8E9FF",
  light: "#7FB4FF",
  main: "#2E90FA",
  dark: "#1B5FC1",
  darker: "#0B2E6B",
  contrastText: "#FFFFFF",
};

const SUCCESS = {
  lighter: "#D3FCD2",
  light: "#61D392",
  main: "#22C55E",
  dark: "#128C48",
  darker: "#065E49",
  contrastText: "#FFFFFF",
};

const WARNING = {
  lighter: "#FFF0D6",
  light: "#FFC680",
  main: BRAND.yellow,
  dark: "#C97A16",
  darker: "#7A4100",
  contrastText: BRAND.black,
};

const ERROR = {
  lighter: "#FFE9E6",
  // El coral de marca vive acá: es demasiado claro para texto de validación
  light: BRAND.coral,
  main: "#F0503F",
  dark: "#C22E20",
  darker: "#7A1208",
  contrastText: "#FFFFFF",
};

const COMMON = {
  common: {
    black: "#000000",
    white: "#FFFFFF",
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function groupRowStyle(settings) {
  const isDark = settings.themeMode === "dark";

  return {
    backgroundColor: isDark ? BRAND.darkBg : GREY[300],
    color: isDark ? "#FFFFFF" : GREY[900],
  };
}

export function palette(mode) {
  const light = {
    ...COMMON,
    mode: "light",
    text: {
      primary: GREY[900],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: BRAND.white, // cards  #F8F8F8
      default: BRAND.offWhite, // página #F4F2F3
      neutral: GREY[200], // rellenos sutiles, al nivel de la página
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: "dark",
    text: {
      primary: "#FFFFFF",
      secondary: GREY[400],
      disabled: GREY[600],
    },
    background: {
      paper: BRAND.darkPaper,
      default: BRAND.darkBg,
      neutral: alpha("#FFFFFF", 0.08),
    },
    action: {
      ...COMMON.action,
      active: GREY[400],
    },
  };

  return mode === "light" ? light : dark;
}
